import { useMemo } from 'react';
import { Badge, Switch } from '@vnetwork/ui/primitives';
import { StatusPill } from '@vnetwork/ui/data';
import type { ColumnDef } from '@vnetwork/ui/data';
import type { Watchpoint } from '@/api/healthcheck';
import { RowMenu } from '../../components/RowMenu';
import {
  typeMeta,
  statusTone,
  statusLabel,
  targetSummary,
  fmtSeconds,
  fmtRelTime,
} from '../shared';

const muted = (v: React.ReactNode) => (
  <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>{v}</span>
);

export interface WatchpointColumnHandlers {
  onToggle: (wp: Watchpoint) => void;
  onDelete: (wp: Watchpoint) => void;
  onEdit: (wp: Watchpoint) => void;
  busyId?: string | null;
}

/** Column definitions for the watchpoint DataTable (kept out of the list page). */
export function useWatchpointColumns({
  onToggle,
  onDelete,
  onEdit,
  busyId,
}: WatchpointColumnHandlers): ColumnDef<Watchpoint, unknown>[] {
  return useMemo<ColumnDef<Watchpoint, unknown>[]>(
    () => [
      {
        id: 'name',
        header: 'Name',
        accessorKey: 'name',
        cell: ({ row }) => {
          const wp = row.original;
          const meta = typeMeta(wp.type);
          const Icon = meta.icon;
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', minWidth: 0 }}>
              <Icon size={16} style={{ flexShrink: 0, color: 'var(--vn-fg-muted)' }} />
              <span style={{ fontWeight: 'var(--vn-font-weight-medium)' as React.CSSProperties['fontWeight'], whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {wp.name}
              </span>
            </div>
          );
        },
      },
      {
        id: 'type',
        header: 'Type',
        enableSorting: false,
        cell: ({ row }) => (
          <Badge tone="neutral" appearance="subtle" size="sm">
            {typeMeta(row.original.type).label}
          </Badge>
        ),
      },
      {
        id: 'target',
        header: 'Target',
        enableSorting: false,
        cell: ({ row }) =>
          muted(
            <span style={{ fontFamily: 'var(--vn-font-mono)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'inline-block', maxWidth: 280 }}>
              {targetSummary(row.original.type, row.original.config)}
            </span>,
          ),
      },
      {
        id: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }) => {
          const wp = row.original;
          return (
            <StatusPill tone={statusTone(wp.is_active ? wp.last_status : null)}>
              {statusLabel(wp.last_status, wp.is_active)}
            </StatusPill>
          );
        },
      },
      {
        id: 'interval',
        header: 'Interval',
        enableSorting: false,
        cell: ({ row }) => muted(fmtSeconds(row.original.check_interval)),
      },
      {
        id: 'last_check',
        header: 'Last check',
        enableSorting: false,
        cell: ({ row }) => muted(fmtRelTime(row.original.last_check_at)),
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => {
          const wp = row.original;
          return (
            <div
              style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', justifyContent: 'flex-end' }}
              onClick={(e) => e.stopPropagation()}
            >
              <Switch
                checked={wp.is_active}
                disabled={busyId === wp.id}
                onCheckedChange={() => onToggle(wp)}
                aria-label={wp.is_active ? 'Pause monitor' : 'Resume monitor'}
              />
              <RowMenu
                items={[
                  { label: 'Edit', onSelect: () => onEdit(wp) },
                  { label: 'Delete', danger: true, onSelect: () => onDelete(wp) },
                ]}
              />
            </div>
          );
        },
      },
    ],
    [onToggle, onDelete, onEdit, busyId],
  );
}
